import { UpdateStaticData } from '@store/root-store/static-data/static-data.action';
import {
  AuthActionTypes,
  ChangePassword,
  ChangePasswordFailure,
  ChangePasswordSuccess,
  GetMe,
  GetMeFailure,
  GetMeSuccess,
  LogIn,
  LogInFailure,
  LogInFailureLocked,
  LogInSuccess,
  LogOutSuccess,
  PasswordRequest,
  PasswordRequestFailure,
  PasswordRequestSuccess,
  PasswordReset,
  PasswordResetFailure,
  PasswordResetSuccess,
  SelectAccount,
  UpdateMeFailure,
  UpdateMeSuccess,
} from './auth.actions';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LoadSearchFormService } from '@managerLoadBoard/load-board.service';
import { LocalStorageKeys } from '@app/local-storage-keys';
import { DashboardService } from '@services/dashboard.service';
import { setDstFromLoggedUser } from '@services/utils/dst-helper';
import { getTimeZoneOffset } from '@services/utils/get-timezone-offset';
import { AccountSelectorDialogComponent } from '@auth/layout/account-selector-dialog/account-selector-dialog.component';
import { ErrorResponse } from '@coreModels/error-response';
import { ChangePasswordData } from '@models/change-password-data';
import { Credentials } from '@models/credentials';
import { LoginResponse } from '@models/responses/login.response';
import { MeResponse } from '@models/responses/me.response';
import { User } from '@models/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthService } from '@services/auth.service';
import { FilterService } from '@services/filter.service';
import { NotificationService } from '@services/firebase/notification.service';
import { SnackbarService } from '@services/snackbar.service';
import { ROLE_SUPER_ADMIN } from '@permissions/company.permissions';
import { AppState } from '@store/app.states';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { COMPANIES_USER_EMAILS_LIST } from '@services/utils/global-config';
import { ResetPasswordParams } from '@models/reset-password-params';
import { PermissionService } from '@app/services/core/permission.service';
import { ApiKeyHandler } from '@services/overrides/ApiKeyHandler';
import { getDefaultLanguage, getLanguage, setLanguage } from '@services/utils/locale';
import { TranslateService } from '@ngx-translate/core';
import { setRegion } from '@services/utils/region';
import { RegionIdentifier } from '@enums/region-identifier';
import { LanguageShort } from '@enums/languages';
import { setUnitSystem } from '@services/utils/unit-system';
import { UnitSystemIdentifier } from '@enums/unit-system';

dayjs.extend(utc);
dayjs.extend(timezone);

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private authService: AuthService,
    private permissionService: PermissionService,
    private snackBarService: SnackbarService,
    private router: Router,
    private dialog: MatDialog,
    private dashService: DashboardService,
    private notificationService: NotificationService,
    protected filterService: FilterService,
    private loadSearchFormService: LoadSearchFormService,
    private translateService: TranslateService,
  ) {}

  // Login
  LogIn: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.LOGIN),
      map((action: LogIn) => action.payload),
      switchMap((payload: Credentials) => {
        localStorage.setItem('email', payload.email);
        return this.authService.login(payload).pipe(
          map((data: LoginResponse) => {
            return new LogInSuccess(data);
          }),
          catchError((errorResponse) => {
            if (errorResponse.status === 403) {
              return of(new LogInFailureLocked(new ErrorResponse(errorResponse)));
            }
            if (errorResponse.status === 408) {
              const data = {
                ...payload,
                accounts: errorResponse.error.message,
              } as User;
              return of(new SelectAccount(data));
            }

            if (errorResponse.status === 0) {
              this.snackBarService.danger('Login unsuccessful');
            }

            return of(new LogInFailure(new ErrorResponse(errorResponse)));
          }),
        );
      }),
    ),
  );

  LogInSuccess: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.LOGIN_SUCCESS),
        tap((data: LogInSuccess) => {
          localStorage.removeItem(LocalStorageKeys.ELD_SUPER_PASSWORD);
          this.authService.setUser(data.payload);
          this.store.dispatch(new GetMe());
        }),
      ),
    { dispatch: false },
  );

  // Select account
  SelectAccount: Observable<any> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActionTypes.SELECT_ACCOUNT),
        map((action: SelectAccount) => action.payload),
        tap((payload: User) => {
          this.dialog
            .open(AccountSelectorDialogComponent, {
              data: payload,
            })
            .afterClosed()
            .subscribe((data: Credentials) => {
              if (data) {
                this.store.dispatch(new LogIn(data));
              }
            });
        }),
      );
    },
    { dispatch: false },
  );

  // Get Me
  GetMe: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.GET_ME),
      switchMap(() => {
        return this.authService.me().pipe(
          map((response: MeResponse) => {
            this.store.dispatch(new UpdateStaticData(response.alertsData || {}));
            delete response.staticData;

            const companies = response.companies;
            const utcTime = dayjs().utc().valueOf();
            const timeZone = this.filterService.setFromLogin(companies);
            const timeZoneOffset = getTimeZoneOffset(utcTime, timeZone);
            localStorage.setItem('companies', JSON.stringify(response.companies));

            const additionalCompanyInfo = {};
            this.permissionService.mapCompaniesModuleAndFeaturePermissionsToFlags(
              companies,
              additionalCompanyInfo,
              response.supportAccount,
              response.serviceAccount,
            );

            localStorage.setItem('additionalCompanyInfo', JSON.stringify(additionalCompanyInfo));

            this.permissionService.initModuleStates();

            delete response.companies;

            return new GetMeSuccess({ ...response, timeZoneOffset, timeZone });
          }),
          catchError((errorResponse) => {
            this.store.dispatch(new GetMeFailure(new ErrorResponse(errorResponse)));
            return throwError(errorResponse);
          }),
        );
      }),
    ),
  );

  GetMeSuccess: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.GET_ME_SUCCESS),
        tap((data: GetMeSuccess) => {
          this.authService.setUser({
            ...this.authService.getUser(),
            ...data.payload,
          });

          if (!data.payload.authorities.includes(ROLE_SUPER_ADMIN)) {
            this.notificationService.requestPermission();
          }

          let userSettings: any = {};
          if (data.payload.userSettings) {
            userSettings = data.payload.userSettings;
          }
          const themeOnLog = localStorage.getItem('theme');

          if (themeOnLog) {
            userSettings = {
              ...userSettings,
              appThemeId: themeOnLog,
            };
          }

          const oldLanguage = getLanguage();
          const isFleetAccount = !data.payload.serviceAccount && !data.payload.supportAccount;
          if (isFleetAccount) {
            const language = (data.payload.userSettings as any)?.appLanguageId;
            const region = (data.payload.userSettings as any)?.appRegionId;
            const unitSystem = (data.payload.userSettings as any)?.appUnitSystemId;
            setRegion(region);
            setLanguage(language);
            setUnitSystem(unitSystem);
            this.translateService
              .use(language)
              .subscribe({ error: () => this.translateService.setDefaultLang(getDefaultLanguage()) });
          } else {
            setRegion(RegionIdentifier.Usa);
            setLanguage(LanguageShort.ENGLISH);
            setUnitSystem(UnitSystemIdentifier.IMPERIAL);
          }
          localStorage.setItem(
            'userSettings',
            JSON.stringify({
              ...this.dashService.defaultSettings,
              ...userSettings,
              widgetOrder: this.dashService.getWidgetOrder(userSettings),
            }),
          );

          setDstFromLoggedUser(data.payload);
          const email = localStorage.getItem('email');
          const companiesUserList = COMPANIES_USER_EMAILS_LIST;
          if (
            companiesUserList.includes(email) ||
            ApiKeyHandler.areRealMapsChecked() ||
            oldLanguage !== getLanguage()
          ) {
            // reload for MapsConfig to receive email from Local Storage for API key
            // or when app language has changed
            return location.reload();
          }
          this.router.navigateByUrl(this.authService.getDashboardUrl());
        }),
      ),
    { dispatch: false },
  );

  // Update me
  UpdateMe: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.UPDATE_ME),
      switchMap(() => {
        return this.authService.me().pipe(
          map((response: MeResponse) => {
            this.store.dispatch(new UpdateStaticData(response.alertsData || {}));
            delete response.staticData;

            const companies = response.companies;
            const utcTime = dayjs().utc().valueOf();
            const timeZone = this.filterService.setFromLogin(companies);
            const timeZoneOffset = getTimeZoneOffset(utcTime, timeZone);
            localStorage.setItem('companies', JSON.stringify(response.companies));
            delete response.companies;

            return new UpdateMeSuccess({
              ...response,
              timeZoneOffset,
              timeZone,
            });
          }),
          catchError((errorResponse) => {
            this.store.dispatch(new UpdateMeFailure(new ErrorResponse(errorResponse)));
            return throwError(errorResponse);
          }),
        );
      }),
    ),
  );

  UpdateMeSuccess: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.UPDATE_ME_SUCCESS),
        tap((data: UpdateMeSuccess) => {
          this.authService.setUser({
            ...this.authService.getUser(),
            ...data.payload,
          });

          if (!data.payload.authorities.includes(ROLE_SUPER_ADMIN)) {
            // this.notificationService.requestPermission();
          }

          let userSettings: any = {};
          if (data.payload.userSettings) {
            userSettings = data.payload.userSettings;
          }

          const themeOnLog = localStorage.getItem('theme');
          if (themeOnLog) {
            userSettings = {
              ...userSettings,
              appThemeId: themeOnLog,
            };
          }

          localStorage.setItem(
            'userSettings',
            JSON.stringify({
              ...this.dashService.defaultSettings,
              ...userSettings,
              widgetOrder: this.dashService.getWidgetOrder(userSettings),
            }),
          );

          setDstFromLoggedUser(data.payload);
          const email = localStorage.getItem('email');
          const companiesUserList = COMPANIES_USER_EMAILS_LIST;
          if (companiesUserList.includes(email)) {
            // reload for MapsConfig to receive email from Local Storage for API key
            return location.reload();
          }
        }),
      ),
    { dispatch: false },
  );

  // Password Request
  PasswordRequest: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.PASSWORD_REQUEST),
      map((action: PasswordRequest) => action.payload),
      switchMap((payload: User) => {
        return this.authService.passwordRequest(payload).pipe(
          map((data: User) => new PasswordRequestSuccess(data)),
          catchError((errorResponse) => {
            this.store.dispatch(new PasswordRequestFailure(new ErrorResponse(errorResponse)));
            return throwError(errorResponse);
          }),
        );
      }),
    ),
  );

  // Password Reset
  PasswordReset: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.PASSWORD_RESET),
      map((action: PasswordReset) => action.payload),
      switchMap((payload: ResetPasswordParams) => {
        return this.authService.passwordReset(payload).pipe(
          map((data: User) => new PasswordResetSuccess(data)),
          catchError((errorResponse) => {
            this.store.dispatch(new PasswordResetFailure(errorResponse.error));
            return throwError(errorResponse);
          }),
        );
      }),
    ),
  );

  PasswordResetSuccess: Observable<any> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActionTypes.PASSWORD_RESET_SUCCESS),
        tap(() => {
          this.snackBarService.success('Password changed successfully, You can log in now using the new password');
          setTimeout(() => {
            this.router.navigateByUrl('/auth/login');
          }, 2000);
        }),
      );
    },
    { dispatch: false },
  );

  // Change Password
  ChangePassword: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.CHANGE_PASSWORD),
      switchMap((data: ChangePassword) => {
        return this.authService.changePassword(data.payload).pipe(
          map((response: ChangePasswordData) => new ChangePasswordSuccess(response)),
          catchError((errorResponse) => {
            throwError(errorResponse);
            return of(new ChangePasswordFailure(new ErrorResponse(errorResponse)));
          }),
        );
      }),
    ),
  );

  ChangePasswordSuccess: Observable<any> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActionTypes.CHANGE_PASSWORD_SUCCESS),
        tap(() => {
          this.snackBarService.success('Password changed successfully');
        }),
      );
    },
    { dispatch: false },
  );

  ChangePasswordFailure: Observable<any> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActionTypes.CHANGE_PASSWORD_FAILURE),
        map((errors: ChangePasswordFailure) => {
          try {
            this.snackBarService.danger(errors.payload.errors[0].message);
          } catch (e) {
            // ignore silently
          }
        }),
      );
    },
    { dispatch: false },
  );

  // Logout
  LogOut: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.LOGOUT),
      switchMap((result) => {
        this.loadSearchFormService.deleteStorageItem();

        const userStorage = localStorage.getItem('userSettings');
        const user = localStorage.getItem('user');
        if (user) {
          const userObject = JSON.parse(user);
          const roles = userObject.authorities;
          const isSuperAdmin = roles && roles.includes(ROLE_SUPER_ADMIN);
          if (isSuperAdmin) {
            return of(result);
          }
        }
        let config;
        if (userStorage) {
          config = JSON.parse(userStorage);
        }
        if (!config) {
          return of(result);
        }
        return this.dashService.saveMapConfig(config).pipe(
          catchError((_) => of(result)),
          map((_) => result),
        );
      }),
      switchMap(() => {
        return this.authService.logout().pipe(
          map(() => new LogOutSuccess()),
          catchError(() => {
            return of(new LogOutSuccess());
          }),
        );
      }),
    ),
  );

  LogOutSuccess: Observable<any> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActionTypes.LOGOUT_SUCCESS),
        tap(() => {
          // Unregister FCM service worker
          if (navigator.serviceWorker) {
            navigator.serviceWorker
              .getRegistrations()
              .then((registrations: ServiceWorkerRegistration[]) => {
                for (const registration of registrations) {
                  registration.unregister();
                }
              })
              .finally(() => {
                this.goLoginAndReload();
              });
          } else {
            this.goLoginAndReload();
          }
        }),
      );
    },
    { dispatch: false },
  );

  goLoginAndReload() {
    /* Go to login page and reload */
    /* localStorage.setItem('theme', this.theme); */
    this.router.navigateByUrl('/auth/login').then(() => {
      document.location.href = document.location.pathname;
    });
  }
}
